/**
 * HighlightedText — auto-bolds + colors key BLK PACKS terms inside any
 * descriptive subtitle / body copy. Keeps the canonical brand vocabulary
 * visually consistent across every page (EMERGE, DROP ID™, Verification ID,
 * rarities, etc.).
 *
 * Usage:
 *   <HighlightedText text="Every BLK PACKS card ships with a unique Verification ID, Card ID, and Serial Number." />
 *
 * Pass `extraPhrases` to add page-specific terms (e.g. series names).
 * Pass `className` to control the base wrapper styling.
 */
import React from "react";

// Canonical site vocabulary. Order matters — longer phrases first so the
// tokenizer doesn't split "Drop ID™" into "Drop" + "ID".
//
// Every key term now renders in `brand-gradient-text` (yellow → orange → red)
// to match the LEGENDS hero palette. This single change cascades the brand
// gradient into every subtitle / description across Home, Drops, Series,
// Sets, Challenges, Locker, Marketplace, EMERGE, etc.
const _BRAND_BOLD = "brand-gradient-text font-bold";
const _BRAND_SEMI = "brand-gradient-text font-semibold";
const DEFAULT_PHRASES = [
  // Brand / system names
  { match: /BLK\s*PACKS/gi, cls: "text-white font-bold" },
  { match: /DROP\s*ID™?/g, cls: _BRAND_BOLD },
  { match: /\bEMERGE\b/g, cls: _BRAND_BOLD },
  { match: /\bBLK\s*PAY\b/gi, cls: _BRAND_BOLD },
  { match: /\bLock\s*In\b/gi, cls: _BRAND_SEMI },
  { match: /\bPack\s*Points\b/gi, cls: _BRAND_SEMI },

  // IDs / identifiers
  { match: /Verification\s*ID/gi, cls: _BRAND_SEMI },
  { match: /Card\s*ID/gi, cls: _BRAND_SEMI },
  { match: /Serial\s*Number/gi, cls: _BRAND_SEMI },
  { match: /Certificate\s*of\s*Authenticity/gi, cls: _BRAND_SEMI },
  { match: /\bCOA\b/g, cls: _BRAND_SEMI },
  { match: /QR\s*Code/gi, cls: _BRAND_SEMI },

  // Card forms
  { match: /Physical\s*Card/gi, cls: _BRAND_SEMI },
  { match: /Digital\s*Collectible/gi, cls: _BRAND_SEMI },

  // Rarities
  { match: /\bLegendary\b/gi, cls: _BRAND_BOLD },
  { match: /\bEpic\b/gi, cls: _BRAND_BOLD },
  { match: /\bRare\b(?!\w)/gi, cls: _BRAND_BOLD },
  { match: /\bHolo(graphic)?\b/gi, cls: _BRAND_SEMI },

  // Actions / metals
  { match: /Pack\s*Rip/gi, cls: _BRAND_SEMI },
  { match: /\bAR\s*(Quick\s*Look|experience|preview|mode)?/g, cls: _BRAND_SEMI },
  { match: /\bScan\b/gi, cls: "text-white font-semibold" },
  { match: /\bMint(ed|ing)?\b/gi, cls: _BRAND_SEMI },
  { match: /\bTrade(s|d|ing)?\b/gi, cls: _BRAND_SEMI },
  { match: /\bgold line\b/gi, cls: _BRAND_BOLD },
  { match: /\bgold\b/gi, cls: _BRAND_SEMI },
  { match: /\bsilver line\b/gi, cls: _BRAND_BOLD },
  { match: /\bsilver\b/gi, cls: _BRAND_SEMI },
];

/**
 * Returns an array of segments for the given text, each tagged with the
 * CSS class that should wrap it. A plain segment returns cls = null.
 */
function _tokenize(text, phrases) {
  if (!text) return [];
  // Collect ALL matches across all patterns, then resolve overlaps by
  // keeping the earliest-start / longest match (greedy-earliest).
  const hits = [];
  for (const { match, cls } of phrases) {
    const re = new RegExp(match.source, match.flags);
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m[0].length === 0) { re.lastIndex++; continue; }
      hits.push({ start: m.index, end: m.index + m[0].length, text: m[0], cls });
    }
  }
  hits.sort((a, b) => (a.start - b.start) || (b.end - a.end));

  const merged = [];
  let cursor = -1;
  for (const h of hits) {
    if (h.start >= cursor) {
      merged.push(h);
      cursor = h.end;
    }
  }

  const out = [];
  let pos = 0;
  for (const h of merged) {
    if (h.start > pos) out.push({ text: text.slice(pos, h.start), cls: null });
    out.push({ text: h.text, cls: h.cls });
    pos = h.end;
  }
  if (pos < text.length) out.push({ text: text.slice(pos), cls: null });
  return out;
}

export default function HighlightedText({
  text,
  extraPhrases = [],
  className = "",
  as: Tag = "span",
  testId,
}) {
  const phrases = React.useMemo(
    () => [...extraPhrases, ...DEFAULT_PHRASES],
    [extraPhrases],
  );
  const segments = React.useMemo(() => _tokenize(text || "", phrases), [text, phrases]);
  return (
    <Tag className={className} data-testid={testId}>
      {segments.map((s, i) =>
        s.cls ? (
          <span key={i} className={s.cls}>{s.text}</span>
        ) : (
          <React.Fragment key={i}>{s.text}</React.Fragment>
        ),
      )}
    </Tag>
  );
}
